import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AccountDrawerAccountContextType,
  AccountDrawerCallGuardContextType,
  AccountDrawerContextType,
  AccountDrawerOpenersContextType,
  AccountDrawerSelectionContextType,
  OpenAccountDrawerData,
  OpenActivityLogDrawerData,
  OpenCallLogSideTab,
  OpenContactDrawerData,
  OpenDealDrawerData,
  OpenMeetingDrawerData,
  OpenTaskDrawerData,
  DrawerContent,
  isTaskModalTab,
} from './types';
import {
  AccountDrawerAccountContext,
  AccountDrawerCallGuardContext,
  AccountDrawerContext,
  AccountDrawerOpenersContext,
  AccountDrawerSelectionContext,
} from './contexts';
import useGetAccount from 'hooks/services/account/useGetAccount';
import { Account } from 'crono-fe-common/types/account';
import { useHideTaskConstruction } from 'pages/accountDrawer/hideTaskConstructionContext';
import { useCrmPush } from 'context/crmPush';
import { useLeadScoreAccess } from 'pages/leadScore/hooks/useLeadScoreAccess';
import { useConfirmModal } from 'context/confirmModal';
import { useAccountDrawerController } from 'context/accountDrawerController';
import { LinkedinTabLabel } from 'pages/accountDrawer/linkedinView/index';
import { DrawerPanel } from './DrawerPanel';
import { TaskSplitPanel } from './TaskSplitPanel';

const _getNewForceReload = () => Math.random() * 1_000_000;

const AccountDrawerProvider: FC<PropsWithChildren<{ content: ReactNode }>> = ({
  children,
  content,
}) => {
  const {
    drawerContent,
    setDrawerContent,
    openTaskExecutionDrawer,
    closeDrawer: rawCloseDrawer,
  } = useAccountDrawerController();

  const { accountPushPairs } = useCrmPush();
  const { hasLeadScoreAccess } = useLeadScoreAccess(['Account']);
  // On the CRM id a landed push gave the company: it's the same company, so it
  // reloads with the old data still on screen and the open tab (with anything
  // open in it, like a creation form) stays mounted.
  const isOnPushedAccount =
    !!drawerContent?.accountId &&
    accountPushPairs.some(
      (pair) => pair.newObjectId === drawerContent.accountId,
    );

  const {
    data: accountData,
    isInitialLoading: isLoadingGetAccount,
    error: errorGetAccount,
  } = useGetAccount(
    drawerContent?.accountId,
    true,
    hasLeadScoreAccess,
    true,
    true,
    {
      onError: () => setHideProgressTask(false),
      keepPreviousData: isOnPushedAccount,
    },
  );

  const { setHideProgressTask } = useHideTaskConstruction();

  useEffect(() => {
    if (!drawerContent?.accountId || !accountPushPairs.length) return;

    const matchingPair = accountPushPairs.find(
      (pair) =>
        pair.oldObjectId === drawerContent.accountId && pair.newObjectId,
    );

    if (matchingPair && matchingPair.newObjectId) {
      setDrawerContent({
        ...drawerContent,
        accountId: matchingPair.newObjectId,
      });
    }
  }, [accountPushPairs, drawerContent?.accountId]);

  const [callViewIsDirty, setCallViewIsDirty] = useState(false);

  const [taskSplitPortalNode, setTaskSplitPortalNode] =
    useState<HTMLDivElement | null>(null);

  // Lifted from LinkedinView so both the tab pill and the split drawer can show it.
  const [linkedinTabLabel, setLinkedinTabLabel] =
    useState<LinkedinTabLabel>('Message');

  const callNeedsDiscardConfirm =
    drawerContent?.modalVisible === 'call' && callViewIsDirty;

  const { openModal } = useConfirmModal();

  const openDiscardCallModal = useCallback(
    (
      params: {
        onConfirm?: () => void;
      } = {},
    ) => {
      openModal({
        title: 'Discard call?',
        text: 'You haven’t logged this call yet. If you close this tab now, any unsaved changes will be lost.',
        cancelText: 'Cancel',
        confirmText: 'Discard',
        confirmFunction: () => {
          setCallViewIsDirty(false);
          params.onConfirm?.();
        },
      });
    },
    [openModal, setCallViewIsDirty],
  );

  /** Shown when starting a new task while another task tab is already open. */
  const openTaskInProgressModal = useCallback(
    (params: { onConfirm?: () => void } = {}) => {
      openModal({
        title:
          'A task is already in progress, do you want to perform a new task?',
        text: '',
        confirmFunction: () => {
          setCallViewIsDirty(false);
          params.onConfirm?.();
        },
        cancelFunction: () => null,
      });
    },
    [openModal, setCallViewIsDirty],
  );

  const handleRemoveModal = useCallback(() => {
    setDrawerContent(
      drawerContent
        ? { ...drawerContent, modalVisible: undefined, isTaskSplitView: false }
        : undefined,
    );
  }, [drawerContent, setDrawerContent]);

  const withCallDiscardGuard = useCallback(
    (action: () => void) => {
      if (callNeedsDiscardConfirm) {
        openDiscardCallModal({ onConfirm: action });
        return;
      }
      action();
    },
    [callNeedsDiscardConfirm, openDiscardCallModal],
  );

  /** Guarded close — shows a confirm dialog if a call is in progress. */
  const closeDrawer = useCallback(
    (options?: { force?: boolean }) => {
      if (options?.force) {
        rawCloseDrawer();
      } else {
        withCallDiscardGuard(rawCloseDrawer);
      }
    },
    [rawCloseDrawer, withCallDiscardGuard],
  );

  const updateDrawerContent = useCallback(
    (partial: Partial<DrawerContent>) => {
      setDrawerContent(
        drawerContent ? { ...drawerContent, ...partial } : undefined,
      );
    },
    [drawerContent, setDrawerContent],
  );

  const openContactDrawer = useCallback(
    ({
      accountId,
      prospectId,
      openScore,
      tab,
      forceReload,
    }: OpenContactDrawerData) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: tab ?? 'prospect',
          accountId,
          selectedProspectId: prospectId ?? undefined,
          openScore,
          ...(forceReload ? { forceReload: _getNewForceReload() } : {}),
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const openCallLogDrawer = useCallback(
    ({ accountId, prospectId, activity }: OpenCallLogSideTab) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: 'callLog',
          modalVisible: 'callLog',
          accountId,
          selectedProspectId: prospectId ?? undefined,
          activity,
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const openAccountDrawer = useCallback(
    ({
      accountId,
      forceReload,
      initialTab,
      openScore,
    }: OpenAccountDrawerData) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: initialTab ?? 'account',
          accountId,
          openScore,
          ...(forceReload ? { forceReload: _getNewForceReload() } : {}),
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const openDealDrawer = useCallback(
    ({ opportunityId, accountId, forceReload }: OpenDealDrawerData) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: 'deals',
          modalVisible: 'deals',
          accountId,
          selectedOpportunityId: opportunityId,
          ...(forceReload ? { forceReload: _getNewForceReload() } : {}),
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const openActivityLogDrawer = useCallback(
    ({ accountId, prospectId, activity, logType }: OpenActivityLogDrawerData) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: logType,
          modalVisible: logType,
          accountId,
          selectedProspectId: prospectId ?? undefined,
          activity,
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const openTaskDrawer = useCallback(
    ({
      accountId,
      prospectId,
      taskType,
      initialPhoneSelected,
    }: OpenTaskDrawerData) => {
      const doOpen = () =>
        setDrawerContent({
          tab: taskType,
          modalVisible: taskType,
          accountId,
          selectedProspectId: prospectId ?? undefined,
          task: null,
          initialPhoneSelected,
          taskSessionId: _getNewForceReload(),
        });

      // Starting a new task always asks first when one is already open —
      // regardless of task type or unsaved-changes state — instead of the
      // call-discard guard, which is for navigating away, not task-switching.
      if (isTaskModalTab(drawerContent?.modalVisible)) {
        openTaskInProgressModal({ onConfirm: doOpen });
      } else {
        doOpen();
      }
    },
    [drawerContent?.modalVisible, setDrawerContent, openTaskInProgressModal],
  );

  const openMeetingDrawer = useCallback(
    ({ accountId, prospectId, activity }: OpenMeetingDrawerData) =>
      withCallDiscardGuard(() =>
        setDrawerContent({
          tab: 'meeting',
          modalVisible: 'meeting',
          accountId,
          selectedProspectId: prospectId ?? undefined,
          activity,
        }),
      ),
    [withCallDiscardGuard, setDrawerContent],
  );

  const selectedAccountId = drawerContent?.accountId ?? null;
  const selectedProspectId = drawerContent?.selectedProspectId ?? null;

  const openersValue = useMemo<AccountDrawerOpenersContextType>(
    () => ({
      closeDrawer,
      openContactDrawer,
      openAccountDrawer,
      openCallLogDrawer,
      openDealDrawer,
      openActivityLogDrawer,
      openTaskDrawer,
      openMeetingDrawer,
    }),
    [
      closeDrawer,
      openContactDrawer,
      openAccountDrawer,
      openCallLogDrawer,
      openDealDrawer,
      openActivityLogDrawer,
      openTaskDrawer,
      openMeetingDrawer,
    ],
  );

  const selectionValue = useMemo<AccountDrawerSelectionContextType>(
    () => ({ selectedAccountId, selectedProspectId }),
    [selectedAccountId, selectedProspectId],
  );

  const accountValue = useMemo<AccountDrawerAccountContextType>(
    () => ({
      account: (accountData?.data.data as Account | undefined) ?? null,
      isLoadingGetAccount,
      currentAccountNotExisting: !!errorGetAccount,
    }),
    [accountData, isLoadingGetAccount, errorGetAccount],
  );

  const callGuardValue = useMemo<AccountDrawerCallGuardContextType>(
    () => ({
      callNeedsDiscardConfirm,
      setCallViewIsDirty,
      openDiscardCallModal,
    }),
    [callNeedsDiscardConfirm, setCallViewIsDirty, openDiscardCallModal],
  );

  const legacyValue = useMemo<AccountDrawerContextType>(
    () => ({
      account: accountValue.account,
      selectedAccountId,
      selectedProspectId,
      isLoadingGetAccount,
      currentAccountNotExisting: accountValue.currentAccountNotExisting,
      drawerContent: drawerContent ?? null,
      closeDrawer,
      handleRemoveModal,
      updateDrawerContent,
      openContactDrawer,
      openAccountDrawer,
      openCallLogDrawer,
      openDealDrawer,
      openActivityLogDrawer,
      openTaskDrawer,
      openMeetingDrawer,
      openTaskExecutionDrawer,
      callNeedsDiscardConfirm,
      setCallViewIsDirty,
      openDiscardCallModal,
      taskSplitPortalNode,
      linkedinTabLabel,
      setLinkedinTabLabel,
    }),
    [
      accountValue,
      selectedAccountId,
      selectedProspectId,
      isLoadingGetAccount,
      drawerContent,
      closeDrawer,
      handleRemoveModal,
      updateDrawerContent,
      openContactDrawer,
      openAccountDrawer,
      openCallLogDrawer,
      openDealDrawer,
      openActivityLogDrawer,
      openTaskDrawer,
      openMeetingDrawer,
      openTaskExecutionDrawer,
      callNeedsDiscardConfirm,
      openDiscardCallModal,
      linkedinTabLabel,
      setLinkedinTabLabel,
      taskSplitPortalNode,
    ],
  );

  const isTaskSplitViewOpen =
    !!drawerContent &&
    isTaskModalTab(drawerContent.modalVisible) &&
    !!drawerContent.isTaskSplitView;

  return (
    <AccountDrawerAccountContext.Provider value={accountValue}>
      <AccountDrawerSelectionContext.Provider value={selectionValue}>
        <AccountDrawerCallGuardContext.Provider value={callGuardValue}>
          <AccountDrawerOpenersContext.Provider value={openersValue}>
            <AccountDrawerContext.Provider value={legacyValue}>
              <TaskSplitPanel
                isOpen={isTaskSplitViewOpen}
                onPortalNodeChange={setTaskSplitPortalNode}
              />
              <DrawerPanel
                isOpen={!!drawerContent}
                isSplitView={isTaskSplitViewOpen}
                content={content}
              />
              {children}
            </AccountDrawerContext.Provider>
          </AccountDrawerOpenersContext.Provider>
        </AccountDrawerCallGuardContext.Provider>
      </AccountDrawerSelectionContext.Provider>
    </AccountDrawerAccountContext.Provider>
  );
};

export function useAccountDrawer() {
  const context = useContext(AccountDrawerContext);
  if (context === undefined) {
    throw new Error(
      `useAccountDrawer must be used within a AccountDrawerProvider`,
    );
  }

  return context;
}

/** Stable drawer-opening actions — safe for components rendered many times (e.g. table rows). */
export function useAccountDrawerOpeners() {
  const context = useContext(AccountDrawerOpenersContext);
  if (context === undefined) {
    throw new Error(
      'useAccountDrawerOpeners must be used within a AccountDrawerProvider',
    );
  }

  return context;
}

export function useAccountDrawerSelection() {
  const context = useContext(AccountDrawerSelectionContext);
  if (context === undefined) {
    throw new Error(
      'useAccountDrawerSelection must be used within a AccountDrawerProvider',
    );
  }

  return context;
}

export function useAccountDrawerAccountData() {
  const context = useContext(AccountDrawerAccountContext);
  if (context === undefined) {
    throw new Error(
      'useAccountDrawerAccountData must be used within a AccountDrawerProvider',
    );
  }

  return context;
}

export function useAccountDrawerCallGuard() {
  const context = useContext(AccountDrawerCallGuardContext);
  if (context === undefined) {
    throw new Error(
      'useAccountDrawerCallGuard must be used within a AccountDrawerProvider',
    );
  }

  return context;
}

export default AccountDrawerProvider;
